import { useState } from 'react';
import { failureOf } from '@/api/errors';
import { useContacts, useInvite, usePeople, type HoldAttendees, type HoldResult } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { useToast } from '@/ui/Toast';
import { Button, Kicker, TextInput, TextAction } from '@/ui/primitives';
import { Refusal } from '@/ui/Refusal';

/**
 * Who a meeting is with, and the press that tells them (ADR-0093).
 *
 * **Colleagues and approved counterparties, by id.** There is no field for
 * an address: the platform does not invite strangers, and a contact whose
 * company nobody approved is refused by the server in words. The picker
 * offers what could pass and the server has the last say.
 *
 * **A hold tells nobody.** Picking people here puts them on the hold; only
 * "Send invitations", the person's own press, mails them — as the person,
 * from their Google calendar, under their calendar consent.
 */

export const NOBODY: HoldAttendees = { humanIds: [], contactIds: [] };

export function AttendeePicker({ value, onChange }: { value: HoldAttendees; onChange: (next: HoldAttendees) => void }) {
  const me = useMeOrThrow();
  const people = usePeople();
  const [q, setQ] = useState('');
  const contacts = useContacts(q.trim());
  const colleagues = (people.data?.people ?? []).filter((p) => p.id !== me.humanId && p.state === 'here');
  // What could pass: approved themselves, or employed somewhere that may be.
  const invitable = (contacts.data?.contacts ?? []).filter((c) => c.email && (c.relationship !== 'unapproved' || c.currentCompanyId));
  const toggle = (list: 'humanIds' | 'contactIds', id: string) =>
    onChange({ ...value, [list]: value[list].includes(id) ? value[list].filter((x) => x !== id) : [...value[list], id] });
  const chip = (on: boolean) =>
    `min-h-tap rounded-brand border px-2.5 text-dense ${on ? 'border-accent bg-page text-accent' : 'border-line text-ink hover:border-strong'}`;

  return (
    <div className="flex flex-col gap-3" data-testid="attendee-picker">
      <Kicker>With</Kicker>
      <div className="flex flex-col gap-1.5">
        <span className="text-label uppercase tracking-label text-meta">Colleagues — busy or free, nothing more</span>
        {people.isError && <Refusal compact failure={failureOf(people.error)} />}
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label="Colleagues">
          {colleagues.map((p) => (
            <li key={p.id}>
              <button type="button" aria-pressed={value.humanIds.includes(p.id)} className={chip(value.humanIds.includes(p.id))} onClick={() => toggle('humanIds', p.id)}>
                {p.displayName}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="flex max-w-[360px] flex-col gap-1">
          <span className="text-label uppercase tracking-label text-meta">People we work with</span>
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a contact" placeholder="Erik at ICA" />
        </label>
        {q.trim().length > 0 && contacts.data && invitable.length === 0 && (
          <span className="text-dense text-secondary">Nobody approved by that name. A stranger is approved on their page first.</span>
        )}
        {q.trim().length > 0 && invitable.length > 0 && (
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label="Contacts">
            {invitable.slice(0, 12).map((c) => (
              <li key={c.id}>
                <button type="button" aria-pressed={value.contactIds.includes(c.id)} className={chip(value.contactIds.includes(c.id))} onClick={() => toggle('contactIds', c.id)}>
                  {c.fullName}
                  {c.currentCompanyName ? <span className="text-meta"> · {c.currentCompanyName}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
        {value.contactIds.length > 0 && (
          <span className="text-dense text-secondary">
            {value.contactIds.length === 1 ? 'One contact' : `${value.contactIds.length} contacts`} on it.{' '}
            <TextAction className="underline" onClick={() => onChange({ ...value, contactIds: [] })}>
              Clear
            </TextAction>
          </span>
        )}
      </div>
    </div>
  );
}

const names = (list: readonly string[]) =>
  list.length <= 2 ? list.join(' and ') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;

/** After a hold with people on it: who is on it, where it is, and the press that tells them. */
export function InviteStep({ held, onDone }: { held: HoldResult; onDone: () => void }) {
  const invite = useInvite();
  const toast = useToast();
  return (
    <div className="flex flex-col gap-2 border border-line bg-page p-3" data-testid="invite-step">
      <span className="text-dense">
        Held, with {names(held.attendees)} on it. <strong>Nobody has been told.</strong>
      </span>
      <span className="text-dense text-secondary">
        {held.onGoogle ? 'It is on your Google calendar, with only you on it so far.' : `It is not on your Google calendar yet: ${held.notOnGoogle ?? 'no reason given'}`}
      </span>
      <div className="flex flex-wrap gap-2">
        <Button icon="forward"
          size="row"
          variant="primary"
          disabled={invite.isPending}
          onClick={() =>
            invite.mutate(held.eventId, {
              onSuccess: (r) => {
                toast({ text: `Sent. Google mailed ${r.invited === 1 ? 'the invitation' : `${r.invited} invitations`}, from you.` });
                onDone();
              },
            })
          }
        >Send invitations</Button>
        <Button size="row" variant="tertiary" onClick={onDone}>
          Not yet
        </Button>
      </div>
      {invite.isError && <Refusal compact failure={failureOf(invite.error)} />}
    </div>
  );
}
